import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { PaymentDetails, PaymentConfirmationButton } from '@/components/shared/payment';
import QRCodeDisplay from './registration-qr/QRCodeDisplay';
import ConfirmationCode from './registration-qr/ConfirmationCode';
import PaymentInstructions from './registration-qr/PaymentInstructions';
import QRInstructions from './registration-qr/QRInstructions';
import { generateConfirmationCode, removeAccents } from './registration-qr/utils';
import { formatQRCode } from '@/utils/qrCodeUtils';
import { supabase, isSupabaseAvailable } from '@/lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import RegistrationProgress from './registration-form/RegistrationProgress';
import PrintRegistration from './registration-qr/PrintRegistration';

const ENABLE_SUPABASE_CHECK = true;

interface RegistrationQRCodeProps {
  formData: RegistrationFormValues;
  onGoBack: () => void;
}

const RegistrationQRCode: React.FC<RegistrationQRCodeProps> = ({ formData, onGoBack }) => {
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [qrValue, setQrValue] = useState<string>('');
  const [hasCheckedPayment, setHasCheckedPayment] = useState<boolean>(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState<boolean>(false);
  const [usingSupabase, setUsingSupabase] = useState<boolean>(false);
  const [attendeeData, setAttendeeData] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const fixedContribution = 500000;
  const additionalContribution = typeof formData.additionalContribution === 'number' 
    ? formData.additionalContribution 
    : parseInt(formData.additionalContribution as unknown as string) || 0;
  
  const totalAmount = fixedContribution + additionalContribution;
  
  useEffect(() => {
    if (!location.search.includes('registering')) {
      navigate('?registering=true', { replace: true });
    }
    
    const checkSupabase = async () => {
      if (ENABLE_SUPABASE_CHECK) {
        const supabaseAvailable = await isSupabaseAvailable();
        setUsingSupabase(supabaseAvailable);
      } else {
        setUsingSupabase(false);
      }
    };
    
    checkSupabase();
    
    const code = generateConfirmationCode();
    setConfirmationCode(code);
    
    const qrData = formatQRCode(code);
    setQrValue(qrData);
    
    console.log("Generated QR code value:", qrData);
    
    const newAttendee = {
      name: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      class: formData.class,
      contribution: totalAmount,
      code: code,
      t_shirt_size: formData.t_shirt_size,
      gender: formData.gender,
      attended: false,
      registeredAt: new Date().toISOString()
    };
    
    setAttendeeData(newAttendee);
  }, [formData, additionalContribution, totalAmount, location.search, navigate]);
  
  const handlePaymentConfirmChange = (confirmed: boolean) => {
    setPaymentConfirmed(confirmed);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`w-full ${isMobile ? 'max-w-md' : 'max-w-2xl'} mx-auto`}
    >
      <RegistrationProgress currentStep={2} />
      
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="mb-4"
      >
        <Button
          variant="ghost"
          onClick={onGoBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 group transition-all duration-300"
        >
          <ArrowLeft className="h-4 w-4 group-hover:animate-pulse group-hover:-translate-x-1 transition-transform duration-300" />
          <span>Quay lại chỉnh sửa thông tin</span>
        </Button>
      </motion.div>
      
      <Card className="w-full border-0 shadow-lg bg-gradient-to-b from-white to-blue-50">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
          <CardTitle className="text-center text-xl font-bold">Thông tin đăng ký của bạn</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 p-6">
          <PaymentDetails 
            fixedContribution={fixedContribution}
            additionalContribution={additionalContribution}
            totalAmount={totalAmount}
            tShirtSize={formData.t_shirt_size}
          />
          
          <PaymentInstructions 
            confirmationCode={confirmationCode} 
            hasCheckedPayment={hasCheckedPayment}
            onPaymentConfirmChange={handlePaymentConfirmChange}
            paymentConfirmed={paymentConfirmed}
          />
          
          {hasCheckedPayment && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center gap-4 w-full"
            >
              <QRCodeDisplay qrValue={qrValue} />
              <ConfirmationCode confirmationCode={confirmationCode} />
              <QRInstructions />
              
              <PrintRegistration 
                formData={formData} 
                confirmationCode={confirmationCode}
              />
            </motion.div>
          )}
        </CardContent>
        <CardFooter className="pb-6">
          <PaymentConfirmationButton 
            hasCheckedPayment={hasCheckedPayment}
            setHasCheckedPayment={setHasCheckedPayment}
            fullName={formData.fullName}
            totalAmount={totalAmount}
            attendeeData={attendeeData}
            paymentConfirmed={paymentConfirmed}
          />
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default RegistrationQRCode;
